/* ══════════════════════════════════════════════════════════════════════
   Skön Cosmetics — skon-live-price-core.js
   asset: assets/skon-live-price-core.js
   Modulo: Live Price — motore condiviso
   Versione: 1.2.0 — 24 agosto 2026, ore 15:48 (Europe/Rome)

   CHANGELOG
   ─────────
   1.2.0 (24 ago) — listCents: fallback a promoCents su extra-sconto senza compare_at.
           Prima `listCents` era 0 quando il prodotto NON aveva compareAtPrice:
           il "prezzo pieno" era SOLO quello che Shopify riconosce come
           compare_at. Conseguenza: un prodotto venduto a prezzo pieno (es.
           Booster del Collagene, €32, nessun compare_at) che riceve un
           extra-sconto da CODICE (SK-QUIZ30 → €22,40) restava senza barrato e
           senza pill nei consumer collection/carosello — pctTotal(0, 2240)=0,
           niente pill; e nessun listCents da barrare. Il modulo PDP invece già
           ripiegava su promoCents (listCents = compareAt ? … : promoCents),
           per questo la PDP mostrava €32 barrato + −30% e la collection no:
           i due moduli trattavano l'assenza di compare_at in modo OPPOSTO.
           Ora il core allinea il comportamento alla PDP: se non c'è un vero
           compare_at (> promoCents) MA c'è un extra-sconto reale (allocCents>0),
           `listCents` diventa promoCents — cioè il prezzo pre-codice, che è il
           riferimento corretto da barrare. Se non c'è né compare_at né
           extra-sconto, resta 0 (prodotto a prezzo pieno, nulla da barrare).
           NB (Omnibus): questo introduce un prezzo barrato "pre-sconto" dove il
           tema non ne aveva. È il riferimento pre-codice, non il minimo 30gg:
           l'allineamento al prezzo minimo dei 30 giorni sarà un intervento
           successivo e trasversale (collection, PDP, carosello). Qui garantiamo
           solo la COERENZA di comportamento fra i contesti.
   1.1.0 — Flag di staging APPICCICOSO (sessionStorage).
           Il form dei filtri ricostruisce la query string dai propri campi e
           scarta i parametri estranei: leggendo solo location.search, il
           modulo si spegneva al primo filtro e sembrava rotto.
   1.0.0 — Prima versione.

   COS'È
   ─────
   Il motore che risponde a UNA domanda: «quanto costa davvero questa
   variante, per QUESTO visitatore, adesso?».
   Non lo deduce: lo CHIEDE a Shopify. Costruisce un carrello EFFIMERO via
   Storefront `cartCreate` con le varianti richieste e i codici sconto
   realmente attivi in sessione, e legge `discountAllocations`.

   PERCHÉ `discountAllocations` E NON UNA FORMULA
   ─────────────────────────────────────────────
   L'applicabilità di uno sconto dipende da collection eleggibili, esclusioni,
   usage limit, scadenze, stacking. Replicare quelle regole nel frontend
   significa mantenere una SECONDA fonte di verità che diverge in silenzio.
   Prodotto fuori dalla collection eleggibile → Shopify non alloca → noi non
   mostriamo. Nessuna lista di handle da mantenere.

   COSA NON FA (confini duri — ognuno è una regola del progetto)
   ────────────────────────────────────────────────────────────
   - NON inietta MAI un codice sconto. Legge solo quelli reali in /cart.js.
     Mostrare uno sconto a chi non ce l'ha è una promessa che il checkout
     non mantiene.
   - NON tocca il carrello reale. `cartCreate` crea un carrello effimero lato
     Shopify, mai associato alla sessione del cliente.
   - NON scrive nel DOM. Decide il consumer cosa disegnare.
   - NON filtra su `applicable`. A carrello reale vuoto Shopify lo marca
     false; la sonda è l'autorità perché il carrello lo costruisce LEI, con
     la variante dentro. (skon-fs-suggestions filtra perché vive nel drawer,
     dove il carrello è pieno per definizione: divergenza VOLUTA.)
   - NON calcola lo sconto con una formula propria: lo LEGGE. Nessun
     `round(prezzo × (1−pct))` — vedi §15.1 del master.

   MISURE REALI (probe su tema live, 15 lug 2026)
   ─────────────────────────────────────────────
     1 riga → 632 ms     12 righe → 788 ms     24 righe → 712 ms
   L'overhead è FISSO: le righe sono quasi gratis (+80 ms da 1 a 24).
   Prima chiamata assoluta a freddo ~1730 ms; a canale caldo ~550-600 ms.
   → Conviene UN batch grande. Frammentare in lotti piccoli è PIÙ lento.

   IL CANARINO DELLO SCONTO FISSO (perché il warmup non serve solo a scaldare)
   ──────────────────────────────────────────────────────────────────────────
   Uno sconto order-level a IMPORTO FISSO (es. −10€ sull'ordine) Shopify lo
   spalma proporzionalmente sulle righe: la stessa variante sondata in un
   lotto da 24 riceve una frazione minuscola dello sconto reale → prezzi
   SBAGLIATI, in silenzio, su tutta la vetrina.
   Al 15 lug 2026 su Skön non ne esistono — ma «oggi» non è una garanzia, e
   nessuno ricorderà questa conversazione quando ne nascerà uno.
   Il warmup sonda UNA riga. Per uno sconto PERCENTUALE il rapporto
   alloc/prezzo è identico a 1 e a 24 righe. Per un FISSO crolla
   (fisso/prezzo → fisso/subtotale). Confrontare i due rapporti rileva il
   fisso a costo zero, riusando una chiamata che facciamo comunque.
   Rilevato → il motore si spegne e lascia parlare il price block del tema.
   NB: non è rilevabile DENTRO un singolo batch — anche un fisso produce un
   rapporto costante fra le righe. Serve il confronto 1-riga vs N-righe.

   CONTRATTO PUBBLICO — window.SkonLivePrice
   ─────────────────────────────────────────
     .version                    string
     .isEnabled()                boolean — kill-switch + mode + canarino
     .readActiveCodes()          Promise<string[]>   (memoizzata)
     .warmup(variantId, codes)   Promise<void>       apre il canale + canarino
     .probe(variantIds, codes)   Promise<Map<vid, Row>>
     .isFixedDiscountDetected()  boolean
     .clearCache()               void
     .formatMoney(cents) / .pctRule(a,b) / .pctTotal(a,b)   ← helper canonici

     Row = { listCents, promoCents, allocCents, finalCents, hasExtra }

   TUTTO IN CENTESIMI (interi), come skon-pdp-live-price.js. I float sulle
   valute sono una fonte di errori da un centesimo, e un centesimo per riga
   è un totale sbagliato.

   DEPLOYMENT: caricare DOPO skon-fs-config.js e PRIMA dei consumer.
══════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var VERSION = '1.2.0';

  /* ── CONFIG ─────────────────────────────────────────────────────── */

  function cfg() {
    var root = window.SKON_FS_CONFIG || {};
    return root.collectionLivePrice || {};
  }

  function apiVersion() {
    var root = window.SKON_FS_CONFIG || {};
    return (root.shopify && root.shopify.storefrontApiVersion) || '2026-04';
  }

  function dbg() {
    if (!cfg().debug) return;
    var args = Array.prototype.slice.call(arguments);
    console.log.apply(console, ['[SkonLivePrice]'].concat(args));
  }

  /* ── HELPER CANONICI ────────────────────────────────────────────────
     Identici a skon-pdp-live-price.js. Un settimo consumatore di prezzi
     IMPORTA la formula, non la riscrive (§15.1). */

  function formatMoney(cents) {
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }

  // % di una REGOLA nominale (promo, codice) → ARROTONDATA.
  function pctRule(fromCents, toCents) {
    if (!fromCents || fromCents <= 0) return 0;
    return Math.round(((fromCents - toCents) / fromCents) * 100);
  }

  // Risparmio COMPLESSIVO sul prezzo al pubblico → ARROTONDATO (round).
  // Decisione di Matteo (14 ott 2026): arrotondamento normale ovunque, coerente
  // con pctRule, il badge e il cart drawer. Uniformità del numero su tutto il sito.
  function pctTotal(fromCents, toCents) {
    if (!fromCents || fromCents <= 0) return 0;
    return Math.round(((fromCents - toCents) / fromCents) * 100);
  }

  function toCents(amount) {
    var n = parseFloat(amount);
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  /* ── STATO ──────────────────────────────────────────────────────── */

  var cache = new Map();          // `${vid}|${codesSig}` → Row
  var codesPromise = null;        // memoizzazione di /cart.js
  var fixedDiscountDetected = false;
  var warmupRef = null;           // { vid, ratio } dalla sonda a 1 riga

  var FIXED_RATIO_TOLERANCE = 0.01;   // 1 punto percentuale
  var TEST_FLAG_KEY = 'skon_lp_test';

  function sig(codes) {
    return codes.slice().sort().join(',');
  }

  /* ── KILL-SWITCH ────────────────────────────────────────────────────
     Stesso pattern di `release.mode` in skon-fs-config: il file è
     caricabile in produzione senza rischio, ma resta inerte finché
     mode non passa a 'live'. */

  function isEnabled() {
    var c = cfg();
    if (c.enabled === false) return false;
    if (fixedDiscountDetected) return false;
    if (c.mode === 'staging') {
      // Il flag è APPICCICOSO per sessione, e non è comodità: il form dei
      // filtri ricostruisce la query string dai propri campi e SCARTA i
      // parametri estranei. Leggendo solo location.search, il modulo si
      // spegneva al primo filtro e sembrava rotto quando non lo era.
      // Stesso pattern di skon_fs_progress_unlocked_session.
      try {
        if (new URLSearchParams(window.location.search).has(TEST_FLAG_KEY)) {
          sessionStorage.setItem(TEST_FLAG_KEY, '1');
          return true;
        }
        return sessionStorage.getItem(TEST_FLAG_KEY) === '1';
      } catch (e) {
        return false;
      }
    }
    return c.mode === 'live';
  }

  /* ── CODICI ATTIVI ──────────────────────────────────────────────── */

  function readActiveCodes() {
    if (codesPromise) return codesPromise;
    codesPromise = fetch('/cart.js', { credentials: 'include' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.discount_codes) return [];
        // NIENTE filtro su `applicable`: vedi header.
        return cart.discount_codes
          .map(function (d) { return d && d.code; })
          .filter(Boolean);
      })
      .catch(function (e) {
        dbg('readActiveCodes fallita', e && e.message);
        return [];
      });
    return codesPromise;
  }

  /* ── SONDA ──────────────────────────────────────────────────────────
     `lines(first: 50)` deve essere >= batchSize, altrimenti si perdono
     righe in silenzio (skon-fs-suggestions usa first:10 perché il widget
     ne mostra al massimo 6: là è corretto, qui no). */

  var MUTATION = [
    'mutation SkonLivePriceProbe($input: CartInput!) {',
    '  cartCreate(input: $input) {',
    '    cart { lines(first: 50) { edges { node {',
    '      merchandise { ... on ProductVariant { id } }',
    '      cost {',
    '        amountPerQuantity { amount }',
    '        compareAtAmountPerQuantity { amount }',
    '      }',
    '      discountAllocations { discountedAmount { amount } }',
    '    } } } }',
    '    userErrors { field message }',
    '  }',
    '}'
  ].join('\n');

  function call(variantIds, codes) {
    var shop = (window.Shopify && window.Shopify.shop) || window.location.hostname;
    var url = 'https://' + shop + '/api/' + apiVersion() + '/graphql.json';
    var ctrl = new AbortController();
    var timer = setTimeout(function () { ctrl.abort(); }, cfg().timeoutMs || 6000);

    var body = {
      query: MUTATION,
      variables: {
        input: {
          lines: variantIds.map(function (vid) {
            return { merchandiseId: 'gid://shopify/ProductVariant/' + vid, quantity: 1 };
          }),
          discountCodes: codes
        }
      }
    };

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: ctrl.signal,
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (json) {
        var cc = json && json.data && json.data.cartCreate;
        if (!cc) return null;
        if (cc.userErrors && cc.userErrors.length) dbg('userErrors', cc.userErrors);
        var edges = cc.cart && cc.cart.lines && cc.cart.lines.edges;
        return (edges && edges.length) ? edges : null;
      })
      .catch(function (e) {
        // Degrada in silenzio: la card resta sul prezzo del tema.
        dbg('sonda fallita', e && e.name);
        return null;
      })
      .finally(function () { clearTimeout(timer); });
  }

  function rowsFromEdges(edges) {
    var out = new Map();
    edges.forEach(function (e) {
      var n = e && e.node;
      if (!n || !n.merchandise || !n.merchandise.id || !n.cost) return;
      var vid = String(n.merchandise.id).split('/').pop();
      var promoCents = toCents(n.cost.amountPerQuantity && n.cost.amountPerQuantity.amount);
      var compareCents = n.cost.compareAtAmountPerQuantity
        ? toCents(n.cost.compareAtAmountPerQuantity.amount)
        : 0;
      var allocCents = (n.discountAllocations || []).reduce(function (s, a) {
        return s + toCents(a && a.discountedAmount && a.discountedAmount.amount);
      }, 0);

      /* listCents = il prezzo pieno da barrare.
         1) c'è un vero compare_at (> prezzo di vendita) → è quello, sempre.
            Copre i prodotti in saldo di listino: il barrato resta il listino
            reale anche se sopra si somma un codice.
         2) niente compare_at MA c'è un extra-sconto da codice → il prezzo
            pre-codice (promoCents) è il riferimento da barrare. Allinea il
            comportamento alla PDP, che già ripiega su promoCents. Senza questo
            un prodotto a prezzo pieno scontato solo dal codice non mostrava né
            barrato né pill in collection/carosello (v1.2.0).
         3) né compare_at né extra-sconto → 0: non c'è nulla da barrare. */
      var listCents;
      if (compareCents > promoCents) {
        listCents = compareCents;
      } else if (allocCents > 0) {
        listCents = promoCents;
      } else {
        listCents = 0;
      }

      out.set(vid, {
        listCents: listCents,
        promoCents: promoCents,
        allocCents: allocCents,
        finalCents: promoCents - allocCents,
        hasExtra: allocCents > 0
      });
    });
    return out;
  }

  /* ── CANARINO ───────────────────────────────────────────────────── */

  function checkFixedDiscount(rows) {
    if (!warmupRef || fixedDiscountDetected) return;
    var row = rows.get(warmupRef.vid);
    if (!row || !row.promoCents) return;
    var batchRatio = row.allocCents / row.promoCents;
    // Se entrambi a zero, quella variante è esclusa: non dice nulla.
    if (warmupRef.ratio === 0 && batchRatio === 0) return;
    if (Math.abs(batchRatio - warmupRef.ratio) <= FIXED_RATIO_TOLERANCE) return;

    fixedDiscountDetected = true;
    cache.clear();
    console.warn('[SkonLivePrice] Sconto a importo FISSO rilevato (rapporto '
      + '1-riga ' + warmupRef.ratio.toFixed(4) + ' vs batch ' + batchRatio.toFixed(4)
      + '). Modulo disattivato: resta il prezzo del tema. Vedi header del file.');
  }

  function warmup(variantId, codes) {
    if (!isEnabled() || !variantId || !codes || !codes.length) return Promise.resolve();
    return call([String(variantId)], codes).then(function (edges) {
      if (!edges) return;
      var row = rowsFromEdges(edges).get(String(variantId));
      if (!row || !row.promoCents) return;
      warmupRef = { vid: String(variantId), ratio: row.allocCents / row.promoCents };
      dbg('warmup ok — canale caldo, ratio di riferimento', warmupRef.ratio.toFixed(4));
    });
  }

  /* ── PROBE PUBBLICA ─────────────────────────────────────────────── */

  function probe(variantIds, codes) {
    var result = new Map();
    if (!isEnabled() || !codes || !codes.length || !variantIds || !variantIds.length) {
      return Promise.resolve(result);
    }

    var s = sig(codes);
    var missing = [];
    variantIds.forEach(function (vid) {
      var key = vid + '|' + s;
      if (cache.has(key)) result.set(vid, cache.get(key));
      else if (missing.indexOf(vid) === -1) missing.push(vid);
    });
    if (!missing.length) return Promise.resolve(result);

    var max = cfg().batchSize || 24;
    var chunks = [];
    for (var i = 0; i < missing.length; i += max) chunks.push(missing.slice(i, i + max));

    return chunks.reduce(function (chain, chunk) {
      return chain.then(function () {
        if (fixedDiscountDetected) return;
        return call(chunk, codes).then(function (edges) {
          if (!edges) return;           // degrada: prezzo tema, nessuno stato a metà
          var rows = rowsFromEdges(edges);
          checkFixedDiscount(rows);
          if (fixedDiscountDetected) { result.clear(); return; }
          rows.forEach(function (row, vid) {
            cache.set(vid + '|' + s, row);
            result.set(vid, row);
          });
        });
      });
    }, Promise.resolve()).then(function () {
      return fixedDiscountDetected ? new Map() : result;
    });
  }

  /* ── EXPORT ─────────────────────────────────────────────────────── */

  window.SkonLivePrice = {
    version: VERSION,
    isEnabled: isEnabled,
    readActiveCodes: readActiveCodes,
    warmup: warmup,
    probe: probe,
    isFixedDiscountDetected: function () { return fixedDiscountDetected; },
    clearCache: function () { cache.clear(); codesPromise = null; },
    formatMoney: formatMoney,
    pctRule: pctRule,
    pctTotal: pctTotal
  };
})();